/* Every uptime monitor, worst first, each linking to its detail page. ?company=vidmax,pebble narrows the list using
   the same company directory and resolver as the home dashboard's company filter. */
import { Link, useNavigate, useSearch } from "@tanstack/react-router"
import { useMemo } from "react"

import { trpc } from "../../lib/trpc"
import { EVerdict } from "../HomeDashboardScreen/homeDashboard.types"
import type { ICompanyEntry } from "../HomeDashboardScreen/homeDashboard.types"
import { ago, parseCompanyParam, resolveCompany } from "../HomeDashboardScreen/homeDashboardModel"
import { monitorState } from "../HomeDashboardScreen/widgets/monitorsWidget"
import type { TMonitor } from "../HomeDashboardScreen/widgets/monitorsWidget"

const REFETCH_MS = 60_000
const DIRECTORY_STALE_MS = 5 * 60_000
const SECOND_MS = 1000
const NO_COMPANIES: ICompanyEntry[] = []
/** Batched requests share the Worker's one Postgres socket and can hang each other (lib/trpc.ts); send each alone */
const UNBATCHED = { trpc: { context: { skipBatch: true } } } as const

const VERDICT_RANK: Record<EVerdict, number> = {
  [EVerdict.ACT]: 0,
  [EVerdict.WATCH]: 1,
  [EVerdict.UNKNOWN]: 2,
  [EVerdict.CLEAR]: 3,
}

const PILL_CLASS: Record<EVerdict, string> = {
  [EVerdict.ACT]: "bg-destructive/15 text-destructive",
  [EVerdict.WATCH]: "bg-secondary text-foreground",
  [EVerdict.UNKNOWN]: "bg-secondary text-muted-foreground",
  [EVerdict.CLEAR]: "bg-primary/10 text-primary",
}

const formatMs = (ms: number | null | undefined) => {
  if (ms === null || ms === undefined) return "—"
  return ms < SECOND_MS ? `${ms}ms` : `${(ms / SECOND_MS).toFixed(1)}s`
}

interface IRow {
  monitor: TMonitor
  verdict: EVerdict
  pill: string
  company: string | null
}

export default function UptimeMonitorsScreen() {
  const navigate = useNavigate()
  const { company } = useSearch({ strict: false }) as { company?: unknown }
  const selected = useMemo(() => parseCompanyParam(company), [company])
  const list = trpc.uptimeMonitors.list.useQuery(undefined, { ...UNBATCHED, refetchInterval: REFETCH_MS })
  const directoryQuery = trpc.companyHome.getDirectory.useQuery(undefined, {
    ...UNBATCHED,
    staleTime: DIRECTORY_STALE_MS,
  })
  const directory = directoryQuery.data ?? NO_COMPANIES

  const rows = useMemo<IRow[]>(() => {
    const now = Date.now()
    return (list.data ?? [])
      .map((monitor) => ({
        monitor,
        ...monitorState(monitor, now),
        company: resolveCompany(monitor.service, directory),
      }))
      .sort((a, b) => VERDICT_RANK[a.verdict] - VERDICT_RANK[b.verdict] || a.monitor.name.localeCompare(b.monitor.name))
  }, [list.data, directory])

  const companiesWithMonitors = directory.filter((entry) => rows.some((row) => row.company === entry.slug))
  const shown = selected.length ? rows.filter((row) => row.company && selected.includes(row.company)) : rows
  const down = shown.filter((row) => row.verdict === EVerdict.ACT).length

  const toggleCompany = (slug: string) => {
    const next = selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]
    void navigate({
      to: "/devtools/uptime",
      search: (prev: Record<string, unknown>) => ({ ...prev, company: next.length ? next.join(",") : undefined }),
    })
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4 p-4 lg:p-8">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="m-0 text-2xl font-semibold">Uptime Monitors</h1>
        <span className="text-sm text-muted-foreground">
          {list.data ? `${down} down · ${shown.length} monitors` : ""}
        </span>
        <Link to="/status" className="ml-auto text-sm text-muted-foreground underline hover:text-foreground">
          Public status page
        </Link>
      </div>

      {companiesWithMonitors.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by company">
          {companiesWithMonitors.map((entry) => (
            <button
              key={entry.slug}
              type="button"
              aria-pressed={selected.includes(entry.slug)}
              onClick={() => toggleCompany(entry.slug)}
              className={`rounded-full border px-3 py-1 text-xs ${
                selected.includes(entry.slug)
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {entry.name}
            </button>
          ))}
        </div>
      )}

      {list.isLoading ? (
        <div className="rounded border-2 border-border bg-background p-8 text-center text-muted-foreground">
          <div className="mb-2 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          <div>Loading…</div>
        </div>
      ) : list.error ? (
        <p className="text-destructive">{list.error.message}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Monitor</th>
                <th className="px-3 py-2 font-medium">Service</th>
                <th className="px-3 py-2 text-right font-medium">Uptime 24h</th>
                <th className="px-3 py-2 text-right font-medium">Latency</th>
                <th className="px-3 py-2 text-right font-medium">Checked</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(({ monitor, verdict, pill }) => (
                <tr key={monitor.id} className="border-t border-border hover:bg-secondary/50">
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PILL_CLASS[verdict]}`}>
                      {pill}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to="/devtools/uptime/$monitorId"
                      params={{ monitorId: monitor.id }}
                      className="font-medium hover:underline"
                    >
                      {monitor.name}
                    </Link>
                    {monitor.status === "down" && monitor.lastCheck?.error && (
                      <div className="break-all text-xs text-destructive">{monitor.lastCheck.error}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {monitor.service}
                    {!monitor.enabled && " · paused"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{monitor.uptime24h}%</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMs(monitor.lastCheck?.latencyMs)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {monitor.lastCheck ? ago(monitor.lastCheck.checkedAt, Date.now()) : "never"}
                  </td>
                </tr>
              ))}
              {!shown.length && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    No monitors{selected.length ? " for this company" : ""}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
